type BikeLike = Record<string, unknown> | null | undefined

const BIKE_CAN_SOC_1_KEYS = [
  'canSoc1',
  'can_soc1',
  'can_soc_1',
  'canStateOfCharge1',
  'can_state_of_charge_1',
  'CAN_SOC_1',
  'packSoc1',
  'pack_soc_1',
  'battery_soc_1',
  'batterySoc1',
] as const

const BIKE_CAN_SOC_2_KEYS = [
  'canSoc2',
  'can_soc2',
  'can_soc_2',
  'canStateOfCharge2',
  'can_state_of_charge_2',
  'CAN_SOC_2',
  'packSoc2',
  'pack_soc_2',
  'battery_soc_2',
  'batterySoc2',
] as const

const BIKE_REMAINING_RANGE_KEYS = [
  'canRemainingRange',
  'can_remaining_range',
  'canRemainingRangeMaximum',
  'can_remaining_range_maximum',
  'remainingRangeMax',
  'range_km_max',
  'rangeKmMax',
  'max_range_km',
  'range_max_km',
  'estimated_range_km',
  'range_max',
  'maxRange',
] as const

function numberFrom(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function clampSocPercent(value: number): number {
  return Math.round(Math.min(100, Math.max(0, value)))
}

function readNumberFromRecord(record: Record<string, unknown>, keys: readonly string[]): number | undefined {
  for (const key of keys) {
    const n = numberFrom(record[key])
    if (n != null) return n
  }
  return undefined
}

function bikeRecord(row: BikeLike): Record<string, unknown> | null {
  if (!row || typeof row !== 'object') return null
  const nested = row.bike && typeof row.bike === 'object' ? (row.bike as Record<string, unknown>) : row
  return nested
}

function readTrimmedString(row: BikeLike, ...keys: string[]): string {
  const record = bikeRecord(row)
  if (!record) return ''
  for (const key of keys) {
    const value = record[key]
    if (value != null && String(value).trim() !== '') return String(value).trim()
  }
  return ''
}

/** Resolve go_metro_bikes registry id (preferred assignment key). */
export function readBikeRegistryId(row: BikeLike): number | undefined {
  const record = bikeRecord(row)
  if (!record) return undefined
  for (const key of ['go_metro_bike_id', 'bike_id', 'id']) {
    const value = record[key]
    if (value != null && value !== '' && Number.isFinite(Number(value))) return Number(value)
  }
  return undefined
}

/** Resolve bike device IMEI from API row. */
export function readBikeDeviceId(row: BikeLike): string {
  return readTrimmedString(row, 'deviceId', 'device_id', 'ident', 'imei')
}

/** Resolve bike number plate from API row (number_plate, licensePlate, license_plate). */
export function readBikeNumberPlate(row: BikeLike): string {
  return readTrimmedString(row, 'number_plate', 'licensePlate', 'license_plate')
}

/** Resolve bike VIN from API row. */
export function readBikeVin(row: BikeLike): string {
  return readTrimmedString(row, 'vin')
}

/** Resolve bike SIM/cell number from API row. */
export function readBikeCell(row: BikeLike): string {
  return readTrimmedString(row, 'cell', 'phone', 'sim')
}

/** Dual-pack CAN SOC 1 (0–100) from API / telemetry row. */
export function readBikeCanSoc1(row: BikeLike): number | undefined {
  const record = bikeRecord(row)
  if (!record) return undefined
  const n = readNumberFromRecord(record, BIKE_CAN_SOC_1_KEYS)
  return n != null ? clampSocPercent(n) : undefined
}

/** Dual-pack CAN SOC 2 (0–100) from API / telemetry row. */
export function readBikeCanSoc2(row: BikeLike): number | undefined {
  const record = bikeRecord(row)
  if (!record) return undefined
  const n = readNumberFromRecord(record, BIKE_CAN_SOC_2_KEYS)
  return n != null ? clampSocPercent(n) : undefined
}

/** Remaining range in km from bike CAN / telematics payload. */
export function readBikeRemainingRangeKm(row: BikeLike): number | undefined {
  const record = bikeRecord(row)
  if (!record) return undefined
  const n = readNumberFromRecord(record, BIKE_REMAINING_RANGE_KEYS)
  if (n == null) return undefined
  return Math.round(n * 10) / 10
}

/** UI label for one bike pack SOC, or em dash when absent. */
export function formatBikeCanSocPercent(row: BikeLike, pack: 1 | 2): string {
  const n = pack === 1 ? readBikeCanSoc1(row) : readBikeCanSoc2(row)
  return n != null ? `${n}%` : '—'
}

export function withNormalizedBikeIdentityFields<T extends Record<string, unknown>>(row: T): T {
  const numberPlate = readBikeNumberPlate(row)
  const vin = readBikeVin(row)
  const registryId = readBikeRegistryId(row)
  const deviceId = readBikeDeviceId(row)
  const cell = readBikeCell(row)
  return {
    ...row,
    ...(deviceId ? { deviceId, device_id: deviceId, ident: row.ident != null ? row.ident : deviceId } : {}),
    ...(registryId != null
      ? { id: registryId, bike_id: registryId, go_metro_bike_id: registryId }
      : {}),
    ...(numberPlate ? { number_plate: numberPlate, licensePlate: numberPlate, license_plate: numberPlate } : {}),
    ...(vin ? { vin } : {}),
    ...(cell ? { cell } : {}),
  }
}

function assignmentKey(value: unknown): string {
  if (value == null || value === '') return ''
  return String(value).trim().toLowerCase()
}

/** True when driver row is linked to the given bike (registry id, IMEI, plate, or valt id). */
export function driverMatchesBikeAssignment(driver: Record<string, unknown>, bike: BikeLike): boolean {
  const bikeRegId = readBikeRegistryId(bike)
  const driverRegRaw = driver.go_metro_bike_id ?? driver.bike_id
  if (
    bikeRegId != null &&
    driverRegRaw != null &&
    driverRegRaw !== '' &&
    Number(driverRegRaw) === bikeRegId
  ) {
    return true
  }
  const bikeImei = readBikeDeviceId(bike)
  const driverImei = readTrimmedString(driver, 'bike_device_id', 'bikeDeviceId', 'bike_imei', 'bikeImei')
  if (bikeImei !== '' && driverImei !== '' && driverImei === bikeImei) {
    return true
  }
  if (driverImei !== '' && bikeRegId != null && driverImei === String(bikeRegId)) {
    return true
  }
  if (driverRegRaw != null && driverRegRaw !== '' && bikeImei !== '' && String(driverRegRaw) === bikeImei) {
    return true
  }
  const bikePlate = readBikeNumberPlate(bike)
  if (driverImei !== '' && bikePlate !== '' && assignmentKey(driverImei) === assignmentKey(bikePlate)) {
    return true
  }
  const bikeName = readTrimmedString(bike, 'name')
  if (driverImei !== '' && bikeName !== '' && assignmentKey(driverImei) === assignmentKey(bikeName)) {
    return true
  }
  return false
}

/** Select/list key: prefer registry id, fallback to IMEI. */
export function getBikeAssignmentSelectValue(row: BikeLike): string {
  const registryId = readBikeRegistryId(row)
  if (registryId != null) return String(registryId)
  return readBikeDeviceId(row)
}
